import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, type PropsWithChildren } from "react";
import { api } from "../api/client";

export type Session = {
  subjectType: "staff" | "citizen";
  actorId: string;
  displayName: string;
  householdId: string | null;
  agentId: string | null;
  permissions: string[];
};

type AuthValue = {
  session: Session | null;
  loading: boolean;
  refresh: () => Promise<unknown>;
  loginStaff: (username: string, password: string) => Promise<void>;
  loginCitizen: (phone: string, pin: string) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: string) => boolean;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ["session"],
    queryFn: async () => (await api<Session>("/auth/session")).data,
    retry: false,
    staleTime: 60_000
  });

  const value: AuthValue = {
    session: query.data ?? null,
    loading: query.isPending,
    refresh: () => query.refetch(),
    loginStaff: async (username, password) => {
      await api("/auth/staff/login", { method: "POST", body: { username, password } });
      await queryClient.invalidateQueries({ queryKey: ["session"] });
    },
    loginCitizen: async (phone, pin) => {
      await api("/auth/citizen/login", { method: "POST", body: { phone, pin } });
      await queryClient.invalidateQueries({ queryKey: ["session"] });
    },
    logout: async () => {
      await api("/auth/logout", { method: "POST" });
      await queryClient.cancelQueries();
      queryClient.removeQueries({
        predicate: (cachedQuery) => cachedQuery.queryKey[0] !== "session"
      });
      queryClient.setQueryData(["session"], null);
    },
    can: (permission) => query.data?.permissions.includes(permission) ?? false
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("AuthProvider is missing");
  return value;
}
